/**
 * FORJA LAB — CodeAI101 chapter 6, and the subject is this page.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 6.
 *
 * ── AUDITING AN INVENTED SERVICE WOULD HAVE BEEN SPEECH ABOUT SPEECH ──────────────────────────
 * §1: "declares its STATE, TRUST, SENSE, and PROOF — by data, not by speech." Every other lab in
 * this course could take its example from the module. This one could not: a Forja audit of a
 * made-up service would be exactly the thing the chapter refuses. So the subject is the tutor the
 * learner is sitting in — a real system, auditable while it is being discussed.
 *
 * ── IT REPORTS ITS OWN FAILURE, AND THAT IS THE DEMONSTRATION ─────────────────────────────────
 * §5 lists five things proof must carry. This app carries four. There is no confidence band
 * anywhere in it — measured, zero. An audit that came back clean would have proved nothing; the
 * whole value of a declaration is that it can embarrass the thing declaring it. So the gap is on
 * screen, in the same weight as the four that pass, in the lab that teaches why gaps must be
 * declared.
 *
 * ── AND THE NUMBERS ARE RE-TAKEN ──────────────────────────────────────────────────────────────
 * The counts are a dated measurement, because a browser cannot grep its own source. gate-forja.mjs
 * re-measures against the source on every run and fails if a figure has moved — a self-audit nobody
 * re-takes is marketing with a date on it.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import Anchor from './anchor.jsx';
import {
  FOUR, PROOF_ELEMENTS, SELF_AUDIT, carriedCount, CONTRAST, SCALES, costOf,
  THE_LINE, NOT_BROCHURE, PROOF_LOADBEARING, CO_EMITTED, AT_SCALE,
  SOURCE_1, SOURCE_2, SOURCE_3, SOURCE_5, SOURCE_6,
} from './forja.mjs';

export default function ForjaLab() {
  const [stripped, setStripped] = useState(false);
  const [n, setN] = useState(50);
  const c = costOf(n);
  const carried = carriedCount();

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Forja lab · auditing this page</div>
      <h2 className="g-h2 lab-h">Four words, and this app fails one of them</h2>
      <Anchor chapter={6} />
      <p className="g-body"><Value of={cited(THE_LINE, SOURCE_1)} /></p>

      <div className="frj-four">
        {FOUR.map((f) => (
          <div key={f.id} className="frj-card">
            <b>{f.name}</b>
            <p className="g-body g-dim">{f.quote}</p>
          </div>
        ))}
      </div>

      {/* ── PROOF, STRIPPED AND UNSTRIPPED ── */}
      <div className="lab-task">
        <div className="g-label">What an answer looks like with its proof, and without</div>
        <p className="g-body"><b>{PROOF_LOADBEARING}</b> <Value of={cited(CO_EMITTED, SOURCE_5)} /></p>
        <div className="lab-task-head">
          <button className={`g-btn ${!stripped ? 'g-btn-primary' : ''}`} onClick={() => setStripped(false)}>With proof</button>
          <button className={`g-btn ${stripped ? 'g-btn-primary' : ''}`} onClick={() => setStripped(true)}>Strip it</button>
        </div>
        <div className={`frj-answer ${stripped ? 'frj-bare' : ''}`}>
          {stripped ? (
            <>
              <div className="frj-big">420</div>
              <p className="g-body g-dim">That is the whole answer. Correct, and unauditable — you
                cannot tell what it summed, by what rule, from which source, or where to look.</p>
            </>
          ) : (
            <>
              <div className="frj-big"><Value of={computed('420')} /></div>
              <ul className="frj-proof">
                <li><b>Which inputs</b> — the two rows where Day = Mon: 240 and 180.</li>
                <li><b>Which rule</b> — <Value of={cited('Sum the Amount column where Day = Mon.', 'Code101 module 03 §5')} /></li>
                <li><b>Which source</b> — the five facts of module 03 §5, quoted verbatim.</li>
                <li><b>Confidence band</b> — <Value of={absent()} /> this app does not emit one.</li>
                <li><b>Where to drill in</b> — Code101 chapter 3, the shapes lab, where you can re-run it.</li>
              </ul>
            </>
          )}
        </div>
      </div>

      {/* ── THE SELF-AUDIT ── */}
      <div className="lab-task">
        <div className="g-label">This app, audited against §5 — measured {SELF_AUDIT.measured_on}</div>
        <div className="lab-readout">
          <div><span className="g-label">Proof elements carried</span>
            <Value of={computed(`${carried} of ${PROOF_ELEMENTS.length}`)} /></div>
          <div><span className="g-label">Values that cite a source</span>
            <Value of={computed(String(SELF_AUDIT.states.cited))} /></div>
          <div><span className="g-label">Values that refuse</span>
            <Value of={computed(String(SELF_AUDIT.states.refused))} /></div>
          <div><span className="g-label">Confidence bands</span>
            <Value of={computed(String(SELF_AUDIT.states.confidence ?? 0))} /></div>
        </div>
        {SELF_AUDIT.proof.map((p) => {
          const el = PROOF_ELEMENTS.find((x) => x.id === p.id);
          return (
            <div key={p.id} className={`frj-row ${p.carried ? '' : 'frj-fail'}`}>
              <span className="frj-tick">{p.carried ? '✓' : '✗'}</span>
              <div>
                <b><Value of={cited(el.text, SOURCE_5)} /></b>
                <p className="g-body g-dim">{p.how}</p>
              </div>
            </div>
          );
        })}
        <p className="g-body frj-punch">
          An audit that came back clean would have proved nothing. The value of a declaration is that
          it can embarrass the thing declaring it — so the gap is here, in the lab that teaches why
          gaps get declared.
        </p>
        <p className="g-body g-dim">{SELF_AUDIT.method}</p>
      </div>

      {/* ── §6 THE CONTRAST ── */}
      <div className="lab-task">
        <div className="g-label">Declaration beats inference</div>
        <div className="frj-table">
          <div className="frj-th">Inference-based AI</div>
          <div className="frj-th">Declaration-based AI (Forja)</div>
          {CONTRAST.map((r) => (
            <ContrastRow key={r.inference} row={r} />
          ))}
        </div>
        <div className="lab-task-head" style={{ marginTop: 'var(--g-3)' }}>
          <span className="g-label">How many services?</span>
          {SCALES.map((s) => (
            <button key={s} className={`g-btn ${n === s ? 'g-btn-primary' : ''}`} onClick={() => setN(s)}>{s}</button>
          ))}
        </div>
        <div className="lab-readout">
          <div><span className="g-label">Answer one question by inference</span>
            <Value of={computed(`${c.byInference} human readings`)} /></div>
          <div><span className="g-label">…by declaration</span>
            <Value of={computed(`${c.byDeclaration} query`)} /></div>
        </div>
        <p className="g-body"><Value of={cited(AT_SCALE, SOURCE_6)} /></p>
      </div>

      <p className="g-body g-dim lab-why">
        The subject of this lab is this app, because auditing an invented service would be speech
        about speech — the thing the chapter refuses. <Value of={cited(NOT_BROCHURE, SOURCE_2)} />
        <br />
        The counts are a dated measurement, not a live one: a browser cannot read its own source.
        They are re-taken against the source by a gate on every run, and it fails if any figure has
        moved — a self-audit nobody re-takes is marketing with a date on it. And the two numbers
        above are not a speed claim; they are the definition of the two approaches, counted. No time
        or money is attached, because the module attaches none.
      </p>
    </div>
  );
}

function ContrastRow({ row }) {
  return (
    <>
      <div className="frj-td frj-inf"><Value of={cited(row.inference, SOURCE_6)} /></div>
      <div className="frj-td frj-dec"><Value of={cited(row.declaration, SOURCE_6)} /></div>
    </>
  );
}
